import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../hooks/useLanguage';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const RevistasPage = () => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [revistas, setRevistas] = useState([]);
  const [editoriales, setEditoriales] = useState([]);
  const [paises, setPaises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Estados para filtros
  const [buscar, setBuscar] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('A');
  const [editorialFiltro, setEditorialFiltro] = useState('');
  const [cuartilFiltro, setCuartilFiltro] = useState('');
  
  // Estados para autocomplete de editorial en filtro
  const [busquedaEditorialFiltro, setBusquedaEditorialFiltro] = useState('');
  const [editorialesFiltroAutocomplete, setEditorialesFiltroAutocomplete] = useState([]);
  const [mostrarDropdownFiltro, setMostrarDropdownFiltro] = useState(false);
  const [editorialFiltroSeleccionada, setEditorialFiltroSeleccionada] = useState(null);
  const [loadingAutocompleteFiltro, setLoadingAutocompleteFiltro] = useState(false);
  
  // Estados para autocomplete de editorial en el modal
  const [busquedaEditorial, setBusquedaEditorial] = useState('');
  const [editorialesAutocomplete, setEditorialesAutocomplete] = useState([]);
  const [mostrarDropdownEditorial, setMostrarDropdownEditorial] = useState(false);
  const [editorialSeleccionada, setEditorialSeleccionada] = useState(null);
  const [loadingAutocomplete, setLoadingAutocomplete] = useState(false);
  
  // Estados para paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const elementosPorPagina = 10;
  
  // Estados para el modal
  const [showModal, setShowModal] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [revistaActual, setRevistaActual] = useState(() => ({
    id: null,
    titulo: '',
    issn: '',
    eissn: '',
    cuartil: '',
    sjr: '',
    indice_h: '',
    acceso_abierto: '',
    tiempo_envio: '',
    id_editorial: '',
    enlace: '',
    frecuencia: '',
    tipo_revision: '',
    apc: '',
    apc_currency: 'USD',
    exenciones: false,
    otros_costos: '',
    en_lista_beall: false,
    overton: false,
    ods: false,
    motivo_descontinuacion: '',
    descontinuado_por: '',
    id_pais: '',
    es_medline: false,
    tipo_revista: '',
    estado: 'A'
  }));

  useEffect(() => {
    // Cargar editoriales y países en paralelo
    Promise.all([cargarEditoriales(), cargarPaises()]).catch(err => {
      console.error('Error cargando datos iniciales:', err);
    });
  }, []);

  // Debounce para búsqueda de editorial en filtro
  useEffect(() => {
    if (busquedaEditorialFiltro.length >= 2) {
      const timer = setTimeout(() => {
        buscarEditorialesFiltro(busquedaEditorialFiltro);
      }, 300);
      
      return () => clearTimeout(timer);
    } else {
      setEditorialesFiltroAutocomplete([]);
    }
  }, [busquedaEditorialFiltro]);

  // Debounce para búsqueda de editorial en modal
  useEffect(() => {
    if (busquedaEditorial.length >= 2) {
      const timer = setTimeout(() => {
        buscarEditoriales(busquedaEditorial);
      }, 300); // 300ms de debounce
      
      return () => clearTimeout(timer);
    } else {
      setEditorialesAutocomplete([]);
    }
  }, [busquedaEditorial]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.autocomplete-editorial')) {
        setMostrarDropdownEditorial(false);
      }
      if (!event.target.closest('.autocomplete-editorial-filtro')) {
        setMostrarDropdownFiltro(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setPaginaActual(1); // Reset a la primera página cuando cambia algún filtro
  }, [estadoFiltro, editorialFiltro, cuartilFiltro, buscar]);

  useEffect(() => {
    cargarRevistas();
  }, [estadoFiltro, editorialFiltro, cuartilFiltro, paginaActual, buscar]);

  const cargarEditoriales = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/editoriales`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { estado: 'A', limit: 500 }
      });
      setEditoriales(response.data);
    } catch (err) {
      console.error('Error al cargar editoriales:', err);
      const errorDetail = err.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        console.error('Detalle:', errorDetail.map(e => e.msg).join(', '));
      }
    }
  };

  const cargarPaises = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/paises`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { estado: 'A' }
      });
      setPaises(response.data);
    } catch (err) {
      console.error('Error al cargar países:', err);
    }
  };

  // Nueva función para buscar editoriales con autocomplete en filtro
  const buscarEditorialesFiltro = async (termino) => {
    if (!termino || termino.length < 2) {
      setEditorialesFiltroAutocomplete([]);
      return;
    }
    
    setLoadingAutocompleteFiltro(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/editoriales`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { 
          estado: 'A', 
          buscar: termino,
          limit: 5  // Solo 5 resultados para el filtro
        }
      });
      setEditorialesFiltroAutocomplete(response.data);
    } catch (err) {
      console.error('Error al buscar editoriales:', err);
      setEditorialesFiltroAutocomplete([]);
    } finally {
      setLoadingAutocompleteFiltro(false);
    }
  };

  // Nueva función para buscar editoriales con autocomplete en modal
  const buscarEditoriales = async (termino) => {
    if (!termino || termino.length < 2) {
      setEditorialesAutocomplete([]);
      return;
    }
    
    setLoadingAutocomplete(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/editoriales`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { 
          estado: 'A', 
          buscar: termino,
          limit: 10
        }
      });
      setEditorialesAutocomplete(response.data);
    } catch (err) {
      console.error('Error al buscar editoriales:', err);
      setEditorialesAutocomplete([]);
    } finally {
      setLoadingAutocomplete(false);
    }
  };

  const cargarRevistas = async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = {
        estado: estadoFiltro,
        skip: (paginaActual - 1) * elementosPorPagina,
        limit: elementosPorPagina
      };
      
      if (buscar.trim()) params.buscar = buscar.trim();
      if (editorialFiltro) params.id_editorial = editorialFiltro;
      if (cuartilFiltro) params.cuartil = cuartilFiltro;
      
      const response = await axios.get(`${API_BASE_URL}/api/revistas`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setRevistas(response.data);
      
      // Obtener el conteo total del header X-Total-Count
      const totalCount = parseInt(response.headers['x-total-count'] || '0');
      setTotalPaginas(Math.ceil(totalCount / elementosPorPagina));
    } catch (err) {
      console.error('Error al cargar revistas:', err);
      const errorDetail = err.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        setError(errorDetail.map(e => e.msg).join(', '));
      } else {
        setError(errorDetail || 'Error al cargar las revistas');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBuscar = (e) => {
    e.preventDefault();
    setPaginaActual(1);
    cargarRevistas();
  };

  const abrirModalCrear = () => {
    setModoEdicion(false);
    setRevistaActual({
      id: null,
      titulo: '',
      issn: '',
      eissn: '',
      cuartil: '',
      sjr: '',
      indice_h: '',
      acceso_abierto: '',
      tiempo_envio: '',
      id_editorial: '',
      enlace: '',
      frecuencia: '',
      tipo_revision: '',
      apc: '',
      apc_currency: 'USD',
      exenciones: false,
      otros_costos: '',
      en_lista_beall: false,
      overton: false,
      ods: false,
      motivo_descontinuacion: '',
      descontinuado_por: '',
      id_pais: '',
      es_medline: false,
      tipo_revista: '',
      estado: 'A'
    });
    setBusquedaEditorial('');
    setEditorialSeleccionada(null);
    setEditorialesAutocomplete([]);
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const abrirModalEditar = (revista) => {
    setModoEdicion(true);
    setRevistaActual({
      ...revista,
      sjr: revista.sjr || '',
      indice_h: revista.indice_h || '',
      cuartil: revista.cuartil || '',
      acceso_abierto: revista.acceso_abierto !== null ? revista.acceso_abierto : '',
      tiempo_envio: revista.tiempo_envio || '',
      apc: revista.apc || '',
      id_editorial: revista.id_editorial || '',
      id_pais: revista.id_pais || '',
      exenciones: revista.exenciones || false,
      en_lista_beall: revista.en_lista_beall || false,
      overton: revista.overton || false,
      ods: revista.ods || false,
      es_medline: revista.es_medline || false,
      otros_costos: revista.otros_costos || '',
      motivo_descontinuacion: revista.motivo_descontinuacion || '',
      descontinuado_por: revista.descontinuado_por || '',
      tipo_revista: revista.tipo_revista || ''
    });
    
    // Establecer editorial seleccionada si existe
    if (revista.id_editorial && revista.editorial_nombre) {
      setEditorialSeleccionada({
        id: revista.id_editorial,
        descripcion: revista.editorial_nombre
      });
      setBusquedaEditorial(revista.editorial_nombre);
    } else {
      setEditorialSeleccionada(null);
      setBusquedaEditorial('');
    }
    
    setEditorialesAutocomplete([]);
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const cerrarModal = () => {
    setShowModal(false);
    setRevistaActual({
      id: null,
      titulo: '',
      issn: '',
      eissn: '',
      cuartil: '',
      sjr: '',
      indice_h: '',
      acceso_abierto: '',
      tiempo_envio: '',
      id_editorial: '',
      enlace: '',
      frecuencia: '',
      tipo_revision: '',
      apc: '',
      apc_currency: 'USD',
      exenciones: false,
      otros_costos: '',
      en_lista_beall: false,
      overton: false,
      ods: false,
      motivo_descontinuacion: '',
      descontinuado_por: '',
      id_pais: '',
      es_medline: false,
      tipo_revista: '',
      estado: 'A'
    });
    setBusquedaEditorial('');
    setEditorialSeleccionada(null);
    setEditorialesAutocomplete([]);
    setError('');
    setSuccess('');
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Preparar datos (convertir strings vacíos a null)
      const datos = {
        titulo: revistaActual.titulo,
        issn: revistaActual.issn || null,
        eissn: revistaActual.eissn || null,
        cuartil: revistaActual.cuartil ? parseInt(revistaActual.cuartil) : null,
        sjr: revistaActual.sjr ? parseFloat(revistaActual.sjr) : null,
        indice_h: revistaActual.indice_h ? parseInt(revistaActual.indice_h) : null,
        acceso_abierto: revistaActual.acceso_abierto !== '' ? parseInt(revistaActual.acceso_abierto) : null,
        tiempo_envio: revistaActual.tiempo_envio ? parseInt(revistaActual.tiempo_envio) : null,
        id_editorial: revistaActual.id_editorial ? parseInt(revistaActual.id_editorial) : null,
        enlace: revistaActual.enlace || null,
        frecuencia: revistaActual.frecuencia || null,
        tipo_revision: revistaActual.tipo_revision || null,
        apc: revistaActual.apc ? parseFloat(revistaActual.apc) : null,
        apc_currency: revistaActual.apc_currency || null,
        exenciones: revistaActual.exenciones || false,
        otros_costos: revistaActual.otros_costos || null,
        en_lista_beall: revistaActual.en_lista_beall || false,
        overton: revistaActual.overton || false,
        ods: revistaActual.ods || false,
        motivo_descontinuacion: revistaActual.motivo_descontinuacion || null,
        descontinuado_por: revistaActual.descontinuado_por || null,
        id_pais: revistaActual.id_pais ? parseInt(revistaActual.id_pais) : null,
        es_medline: revistaActual.es_medline || false,
        tipo_revista: revistaActual.tipo_revista || null
      };
      
      if (modoEdicion) {
        datos.estado = revistaActual.estado;
        await axios.put(
          `${API_BASE_URL}/api/revistas/${revistaActual.id}`,
          datos,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Revista actualizada correctamente');
      } else {
        await axios.post(
          `${API_BASE_URL}/api/revistas`,
          datos,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Revista creada correctamente');
      }
      
      await cargarRevistas();
      cerrarModal();
    } catch (err) {
      console.error('Error al guardar revista:', err);
      const errorDetail = err.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        setError(errorDetail.map(e => e.msg).join(', '));
      } else {
        setError(errorDetail || 'Error al guardar la revista');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Está seguro de que desea eliminar esta revista? Esta acción marcará el registro como inactivo.')) {
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await axios.delete(`${API_BASE_URL}/api/revistas/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Revista eliminada correctamente');
      await cargarRevistas();
    } catch (err) {
      console.error('Error al eliminar revista:', err);
      const errorDetail = err.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        setError(errorDetail.map(e => e.msg).join(', '));
      } else {
        setError(errorDetail || 'Error al eliminar la revista');
      }
    } finally {
      setLoading(false);
    }
  };

  const obtenerBadgeCuartil = (cuartil) => {
    if (!cuartil) return null;
    const colores = { 1: 'success', 2: 'info', 3: 'warning', 4: 'secondary' };
    return <span className={`badge bg-${colores[cuartil] || 'secondary'}`}>Q{cuartil}</span>;
  };

  // Manejar selección de editorial en autocomplete
  const seleccionarEditorial = (editorial) => {
    setEditorialSeleccionada(editorial);
    setBusquedaEditorial(editorial.descripcion);
    setRevistaActual({ ...revistaActual, id_editorial: editorial.id });
    setMostrarDropdownEditorial(false);
    setEditorialesAutocomplete([]);
  };

  // Manejar cambio en input de búsqueda de editorial
  const handleBusquedaEditorialChange = (e) => {
    const valor = e.target.value;
    setBusquedaEditorial(valor);
    setMostrarDropdownEditorial(true);
    
    // Si se borra el campo, limpiar la selección
    if (!valor) {
      setEditorialSeleccionada(null);
      setRevistaActual({ ...revistaActual, id_editorial: '' });
      setEditorialesAutocomplete([]);
    }
  };

  // Limpiar selección de editorial
  const limpiarEditorial = () => {
    setBusquedaEditorial('');
    setEditorialSeleccionada(null);
    setRevistaActual({ ...revistaActual, id_editorial: '' });
    setEditorialesAutocomplete([]);
    setMostrarDropdownEditorial(false);
  };

  // Funciones para el autocomplete del filtro
  const seleccionarEditorialFiltro = (editorial) => {
    setEditorialFiltroSeleccionada(editorial);
    setBusquedaEditorialFiltro(editorial.descripcion);
    setEditorialFiltro(editorial.id);
    setMostrarDropdownFiltro(false);
    setEditorialesFiltroAutocomplete([]);
  };

  const handleBusquedaEditorialFiltroChange = (e) => {
    const valor = e.target.value;
    setBusquedaEditorialFiltro(valor);
    setMostrarDropdownFiltro(true);
    
    if (!valor) {
      setEditorialFiltroSeleccionada(null);
      setEditorialFiltro('');
      setEditorialesFiltroAutocomplete([]);
    }
  };

  const limpiarEditorialFiltro = () => {
    setBusquedaEditorialFiltro('');
    setEditorialFiltroSeleccionada(null);
    setEditorialFiltro('');
    setEditorialesFiltroAutocomplete([]);
    setMostrarDropdownFiltro(false);
  };

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col">
          <h2 className="mb-0">
            <i className="bi bi-journal-text me-2"></i>
            {t('journals.title')}
          </h2>
          <p className="text-muted">{t('journals.subtitle')}</p>
        </div>
      </div>

      {/* Alertas */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}
      
      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="bi bi-check-circle-fill me-2"></i>
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
        </div>
      )}

      {/* Barra de herramientas */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <form onSubmit={handleBuscar}>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t('journals.search')}
                    value={buscar}
                    onChange={(e) => setBuscar(e.target.value)}
                  />
                  <button className="btn btn-outline-secondary" type="submit">
                    <i className="bi bi-search"></i>
                  </button>
                </div>
              </form>
            </div>
            
            <div className="col-md-6 text-end">
              <button className="btn btn-primary" onClick={abrirModalCrear}>
                <i className="bi bi-plus-circle me-2"></i>
                {t('journals.create')}
              </button>
            </div>
          </div>
          
          <div className="row g-3">
            <div className="col-md-3">
              <select
                className="form-select"
                value={estadoFiltro}
                onChange={(e) => setEstadoFiltro(e.target.value)}
              >
                <option value="A">{t('publishers.filters.active')}</option>
                <option value="I">{t('publishers.filters.inactive')}</option>
              </select>
            </div>
            
            <div className="col-md-4">
              <div className="position-relative autocomplete-editorial-filtro">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t('journals.filters.searchPublisher')}
                    value={busquedaEditorialFiltro}
                    onChange={handleBusquedaEditorialFiltroChange}
                    onFocus={() => setMostrarDropdownFiltro(true)}
                    autoComplete="off"
                  />
                  {editorialFiltroSeleccionada && (
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={limpiarEditorialFiltro}
                      title={t('common.cancel')}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  )}
                  {loadingAutocompleteFiltro && (
                    <span className="input-group-text">
                      <span className="spinner-border spinner-border-sm" role="status"></span>
                    </span>
                  )}
                </div>
                
                {/* Dropdown de resultados */}
                {mostrarDropdownFiltro && editorialesFiltroAutocomplete.length > 0 && (
                  <div 
                    className="position-absolute w-100 bg-white border rounded shadow-sm mt-1" 
                    style={{ zIndex: 1050, maxHeight: '200px', overflowY: 'auto' }}
                  >
                    <ul className="list-group list-group-flush">
                      {editorialesFiltroAutocomplete.map((editorial) => (
                        <li
                          key={editorial.id}
                          className="list-group-item list-group-item-action"
                          style={{ cursor: 'pointer' }}
                          onClick={() => seleccionarEditorialFiltro(editorial)}
                        >
                          {editorial.descripcion}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Mensaje cuando no hay resultados */}
                {mostrarDropdownFiltro && busquedaEditorialFiltro.length >= 2 && 
                 !loadingAutocompleteFiltro && editorialesFiltroAutocomplete.length === 0 && (
                  <div 
                    className="position-absolute w-100 bg-white border rounded shadow-sm mt-1 p-2 text-center text-muted" 
                    style={{ zIndex: 1050 }}
                  >
                    <small>No se encontraron editoriales</small>
                  </div>
                )}
                
                {/* Indicador de editorial seleccionada */}
                {editorialFiltroSeleccionada && (
                  <small className="text-success d-block mt-1">
                    <i className="bi bi-check-circle-fill me-1"></i>
                    {editorialFiltroSeleccionada.descripcion}
                  </small>
                )}
              </div>
            </div>
            
            <div className="col-md-2">
              <select
                className="form-select"
                value={cuartilFiltro}
                onChange={(e) => setCuartilFiltro(e.target.value)}
              >
                <option value="">{t('journals.filters.allQuartiles')}</option>
                <option value="1">Q1</option>
                <option value="2">Q2</option>
                <option value="3">Q3</option>
                <option value="4">Q4</option>
              </select>
            </div>
          </div>
        </div>
      </div>

    {/* Tabla de revistas */}
        <div className="card">
          <div className="card-body">
            {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t('common.loading')}</span>
              </div>
            </div>
            ) : revistas.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox display-4 d-block mb-3"></i>
              <p>{t('journals.noResults')}</p>
            </div>
            ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                <tr>
                  <th style={{ width: '5%' }}>#</th>
                  <th style={{ width: '30%' }}>{t('journals.fields.title')}</th>
                  <th style={{ width: '15%' }}>{t('journals.fields.issn')}</th>
                  <th style={{ width: '20%' }}>{t('journals.fields.publisher')}</th>
                  <th style={{ width: '8%' }}>{t('journals.fields.quartile')}</th>
                  <th style={{ width: '8%' }}>{t('common.status')}</th>
                  <th style={{ width: '14%' }} className="text-end">{t('common.actions')}</th>
                </tr>
                </thead>
                <tbody>
                {revistas.map((revista, index) => (
                  <tr key={revista.id}>
                    <td>{(paginaActual - 1) * elementosPorPagina + index + 1}</td>
                    <td>
                    {revista.titulo}
                    {revista.enlace && (
                      <a
                        href={revista.enlace}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ms-2"
                        title={t('journalFinder.card.visitWebsite')}
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                      </a>
                    )}
                    </td>
                    <td>
                    <small className="d-block">{revista.issn || '-'}</small>
                    {revista.eissn && <small className="text-muted">e-ISSN: {revista.eissn}</small>}
                    </td>
                    <td>
                    <small>{revista.editorial_nombre || '-'}</small>
                    </td>
                    <td>{obtenerBadgeCuartil(revista.cuartil)}</td>
                    <td>
                    {revista.estado === 'A' ? (
                      <span className="badge bg-success">{t('common.active')}</span>
                    ) : (
                      <span className="badge bg-secondary">{t('common.inactive')}</span>
                    )}
                    </td>
                    <td className="text-end">
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => abrirModalEditar(revista)}
                      title={t('common.edit')}
                    >
                      <i className="bi bi-pencil"></i>
                    </button>
                    {revista.estado === 'A' && (
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleEliminar(revista.id)}
                        title={t('common.delete')}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    )}
                    </td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
            )}
            
            <div className="mt-3 d-flex justify-content-between align-items-center">
            <small className="text-muted">
              {t('publishers.pagination.showing', {
                start: revistas.length > 0 ? ((paginaActual - 1) * elementosPorPagina + 1) : 0,
                end: Math.min(paginaActual * elementosPorPagina, (paginaActual - 1) * elementosPorPagina + revistas.length),
                total: (paginaActual - 1) * elementosPorPagina + revistas.length
              })}
            </small>
            
            {totalPaginas > 1 && (
              <nav>
                <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${paginaActual === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => setPaginaActual(paginaActual - 1)}
                    disabled={paginaActual === 1}
                  >
                    {t('common.previous')}
                  </button>
                </li>
                
                {[...Array(totalPaginas)].map((_, index) => {
                    const pageNum = index + 1;
                    // Mostrar solo 5 páginas alrededor de la actual
                    if (
                      pageNum === 1 || 
                      pageNum === totalPaginas || 
                      (pageNum >= paginaActual - 2 && pageNum <= paginaActual + 2)
                    ) {
                      return (
                        <li key={pageNum} className={`page-item ${paginaActual === pageNum ? 'active' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => setPaginaActual(pageNum)}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    } else if (
                      pageNum === paginaActual - 3 || 
                      pageNum === paginaActual + 3
                    ) {
                      return <li key={pageNum} className="page-item disabled"><span className="page-link">...</span></li>;
                    }
                    return null;
                  })}
                  
                  <li className={`page-item ${paginaActual === totalPaginas ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={() => setPaginaActual(paginaActual + 1)}
                      disabled={paginaActual === totalPaginas}
                    >
                      {t('common.next')}
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        </div>
      </div>

      {/* Modal para crear/editar */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modoEdicion ? t('journals.modal.editTitle') : t('journals.modal.createTitle')}
                </h5>
                <button type="button" className="btn-close" onClick={cerrarModal}></button>
              </div>
              
              <form onSubmit={handleGuardar}>
                <div className="modal-body">
                  <div className="row g-3">
                    {/* Información básica */}
                    <div className="col-12">
                      <h6 className="border-bottom pb-2">{t('journals.fields.title')}</h6>
                    </div>
                    
                    <div className="col-md-12">
                      <label className="form-label">
                        {t('journals.fields.title')} <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={revistaActual.titulo}
                        onChange={(e) => setRevistaActual({ ...revistaActual, titulo: e.target.value })}
                        required
                        maxLength={255}
                      />
                    </div>
                    
                    <div className="col-md-6">
                      <label className="form-label">{t('journals.fields.issn')}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={revistaActual.issn}
                        onChange={(e) => setRevistaActual({ ...revistaActual, issn: e.target.value })}
                        maxLength={255}
                      />
                    </div>
                    
                    <div className="col-md-6">
                      <label className="form-label">{t('journals.fields.eissn')}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={revistaActual.eissn}
                        onChange={(e) => setRevistaActual({ ...revistaActual, eissn: e.target.value })}
                        maxLength={255}
                      />
                    </div>
                    
                    <div className="col-md-12">
                      <label className="form-label">{t('journals.fields.link')}</label>
                      <input
                        type="url"
                        className="form-control"
                        value={revistaActual.enlace}
                        onChange={(e) => setRevistaActual({ ...revistaActual, enlace: e.target.value })}
                        maxLength={255}
                        placeholder="https://..."
                      />
                    </div>
                    
                    {/* Métricas */}
                    <div className="col-12 mt-4">
                      <h6 className="border-bottom pb-2">{t('journalFinder.card.impactMetrics')}</h6>
                    </div>
                    
                    <div className="col-md-4">
                      <label className="form-label">{t('journals.fields.quartile')}</label>
                      <select
                        className="form-select"
                        value={revistaActual.cuartil}
                        onChange={(e) => setRevistaActual({ ...revistaActual, cuartil: e.target.value })}
                      >
                        <option value="">{t('journals.filters.selectPublisher')}</option>
                        <option value="1">Q1</option>
                        <option value="2">Q2</option>
                        <option value="3">Q3</option>
                        <option value="4">Q4</option>
                      </select>
                    </div>
                    
                    <div className="col-md-4">
                      <label className="form-label">{t('journals.fields.sjr')}</label>
                      <input
                        type="number"
                        className="form-control"
                        value={revistaActual.sjr}
                        onChange={(e) => setRevistaActual({ ...revistaActual, sjr: e.target.value })}
                        min="0"
                        step="0.001"
                        placeholder="Ej: 1.234"
                      />
                      <small className="text-muted">Valor decimal con hasta 3 decimales</small>
                    </div>
                    
                    <div className="col-md-4">
                      <label className="form-label">{t('journals.fields.hIndex')}</label>
                      <input
                        type="number"
                        className="form-control"
                        value={revistaActual.indice_h}
                        onChange={(e) => setRevistaActual({ ...revistaActual, indice_h: e.target.value })}
                        min="0"
                      />
                    </div>
                    
                    <div className="col-md-6">
                      <label className="form-label">{t('journals.fields.openAccess')}</label>
                      <select
                        className="form-select"
                        value={revistaActual.acceso_abierto}
                        onChange={(e) => setRevistaActual({ ...revistaActual, acceso_abierto: e.target.value })}
                      >
                        <option value="">{t('journals.filters.selectPublisher')}</option>
                        <option value="1">{t('common.yes')}</option>
                        <option value="0">{t('common.no')}</option>
                      </select>
                    </div>
                    
                    <div className="col-md-6">
                      <label className="form-label">{t('journals.fields.publisher')}</label>
                      <div className="position-relative autocomplete-editorial">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder={t('journals.filters.searchPublisher')}
                            value={busquedaEditorial}
                            onChange={handleBusquedaEditorialChange}
                            onFocus={() => setMostrarDropdownEditorial(true)}
                            autoComplete="off"
                          />
                          {editorialSeleccionada && (
                            <button
                              className="btn btn-outline-secondary"
                              type="button"
                              onClick={limpiarEditorial}
                              title={t('common.cancel')}
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          )}
                          {loadingAutocomplete && (
                            <span className="input-group-text">
                              <span className="spinner-border spinner-border-sm" role="status"></span>
                            </span>
                          )}
                        </div>
                        
                        {/* Dropdown de resultados */}
                        {mostrarDropdownEditorial && editorialesAutocomplete.length > 0 && (
                          <div 
                            className="position-absolute w-100 bg-white border rounded shadow-sm mt-1" 
                            style={{ zIndex: 1050, maxHeight: '200px', overflowY: 'auto' }}
                          >
                            <ul className="list-group list-group-flush">
                              {editorialesAutocomplete.map((editorial) => (
                                <li
                                  key={editorial.id}
                                  className="list-group-item list-group-item-action"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => seleccionarEditorial(editorial)}
                                >
                                  {editorial.descripcion}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Mensaje cuando no hay resultados */}
                        {mostrarDropdownEditorial && busquedaEditorial.length >= 2 && 
                         !loadingAutocomplete && editorialesAutocomplete.length === 0 && (
                          <div 
                            className="position-absolute w-100 bg-white border rounded shadow-sm mt-1 p-3 text-center text-muted" 
                            style={{ zIndex: 1050 }}
                          >
                            <small>{t('publishers.noResults')}</small>
                          </div>
                        )}
                        
                        {/* Indicador de editorial seleccionada */}
                        {editorialSeleccionada && (
                          <small className="text-success d-block mt-1">
                            <i className="bi bi-check-circle-fill me-1"></i>
                            {t('journals.filters.selectPublisher')}
                          </small>
                        )}
                      </div>
                    </div>
                    
                    {/* Publicación */}
                    <div className="col-12 mt-4">
                      <h6 className="border-bottom pb-2">{t('journals.fields.frequency')}</h6>
                    </div>
                    
                    <div className="col-md-6">
                      <label className="form-label">{t('journals.fields.frequency')}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={revistaActual.frecuencia}
                        onChange={(e) => setRevistaActual({ ...revistaActual, frecuencia: e.target.value })}
                        maxLength={255}
                        placeholder="Ej: Mensual, Trimestral..."
                      />
                    </div>
                    
                    <div className="col-md-6">
                      <label className="form-label">{t('journals.fields.reviewType')}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={revistaActual.tipo_revision}
                        onChange={(e) => setRevistaActual({ ...revistaActual, tipo_revision: e.target.value })}
                        maxLength={255}
                        placeholder="Ej: Doble ciego..."
                      />
                    </div>
                    
                    <div className="col-md-6">
                      <label className="form-label">{t('journals.fields.apc')}</label>
                      <input
                        type="number"
                        className="form-control"
                        value={revistaActual.apc}
                        onChange={(e) => setRevistaActual({ ...revistaActual, apc: e.target.value })}
                        min="0"
                        step="0.01"
                      />
                    </div>
                    
                    <div className="col-md-6">
                      <label className="form-label">{t('journals.fields.apcCurrency')}</label>
                      <select
                        className="form-select"
                        value={revistaActual.apc_currency}
                        onChange={(e) => setRevistaActual({ ...revistaActual, apc_currency: e.target.value })}
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="MXN">MXN</option>
                      </select>
                    </div>
                    
                    <div className="col-md-6">
                      <label className="form-label">{t('journals.fields.submissionTime')}</label>
                      <input
                        type="number"
                        className="form-control"
                        value={revistaActual.tiempo_envio}
                        onChange={(e) => setRevistaActual({ ...revistaActual, tiempo_envio: e.target.value })}
                        min="0"
                        placeholder="Días desde envío hasta publicación"
                      />
                    </div>
                    
                    <div className="col-md-6">
                      <label className="form-label">{t('journals.fields.country')}</label>
                      <select
                        className="form-select"
                        value={revistaActual.id_pais}
                        onChange={(e) => setRevistaActual({ ...revistaActual, id_pais: e.target.value })}
                      >
                        <option value="">{t('journals.filters.selectPublisher')}</option>
                        {paises.map((pais) => (
                          <option key={pais.id} value={pais.id}>{pais.descripcion}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="col-md-6">
                      <label className="form-label">{t('journals.fields.type')}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={revistaActual.tipo_revista}
                        onChange={(e) => setRevistaActual({ ...revistaActual, tipo_revista: e.target.value })}
                        maxLength={255}
                        placeholder="Ej: Científica, Académica..."
                      />
                    </div>
                    
                    {/* Costos y Exenciones */}
                    <div className="col-12 mt-4">
                      <h6 className="border-bottom pb-2">{t('journals.fields.otherCosts')}</h6>
                    </div>
                    
                    <div className="col-md-6">
                      <label className="form-label">{t('journals.fields.otherCosts')}</label>
                      <textarea
                        className="form-control"
                        value={revistaActual.otros_costos}
                        onChange={(e) => setRevistaActual({ ...revistaActual, otros_costos: e.target.value })}
                        maxLength={255}
                        rows="2"
                        placeholder="Descripción de otros costos..."
                      />
                    </div>
                    
                    <div className="col-md-6">
                      <div className="form-check mt-4">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="exenciones"
                          checked={revistaActual.exenciones}
                          onChange={(e) => setRevistaActual({ ...revistaActual, exenciones: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="exenciones">
                          {t('journals.fields.exemptions')}
                        </label>
                      </div>
                    </div>
                    
                    {/* Indexación y Bases de Datos */}
                    <div className="col-12 mt-4">
                      <h6 className="border-bottom pb-2">{t('journalFinder.filters.indexed')}</h6>
                    </div>
                    
                    <div className="col-md-4">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="es_medline"
                          checked={revistaActual.es_medline}
                          onChange={(e) => setRevistaActual({ ...revistaActual, es_medline: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="es_medline">
                          {t('journals.fields.medline')}
                        </label>
                      </div>
                    </div>
                    
                    <div className="col-md-4">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="overton"
                          checked={revistaActual.overton}
                          onChange={(e) => setRevistaActual({ ...revistaActual, overton: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="overton">
                          {t('journals.fields.overton')}
                        </label>
                      </div>
                    </div>
                    
                    <div className="col-md-4">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="ods"
                          checked={revistaActual.ods}
                          onChange={(e) => setRevistaActual({ ...revistaActual, ods: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="ods">
                          {t('journals.fields.ods')}
                        </label>
                      </div>
                    </div>
                    
                    <div className="col-md-6 mt-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="en_lista_beall"
                          checked={revistaActual.en_lista_beall}
                          onChange={(e) => setRevistaActual({ ...revistaActual, en_lista_beall: e.target.checked })}
                        />
                        <label className="form-check-label text-danger" htmlFor="en_lista_beall">
                          {t('journals.fields.beallList')}
                        </label>
                      </div>
                    </div>
                    
                    {/* Descontinuación */}
                    {(modoEdicion && revistaActual.estado === 'I') && (
                      <>
                        <div className="col-12 mt-4">
                          <h6 className="border-bottom pb-2">{t('journals.fields.discontinuationReason')}</h6>
                        </div>
                        
                        <div className="col-md-6">
                          <label className="form-label">{t('journals.fields.discontinuationReason')}</label>
                          <textarea
                            className="form-control"
                            value={revistaActual.motivo_descontinuacion}
                            onChange={(e) => setRevistaActual({ ...revistaActual, motivo_descontinuacion: e.target.value })}
                            maxLength={255}
                            rows="2"
                            placeholder="Razón por la que se descontinuó..."
                          />
                        </div>
                        
                        <div className="col-md-6">
                          <label className="form-label">{t('journals.fields.discontinuedBy')}</label>
                          <input
                            type="text"
                            className="form-control"
                            value={revistaActual.descontinuado_por}
                            onChange={(e) => setRevistaActual({ ...revistaActual, descontinuado_por: e.target.value })}
                            maxLength={255}
                            placeholder="Usuario o entidad que descontinuó"
                          />
                        </div>
                      </>
                    )}
                    
                    {modoEdicion && (
                      <div className="col-md-12 mt-4">
                        <label className="form-label">{t('common.status')}</label>
                        <select
                          className="form-select"
                          value={revistaActual.estado}
                          onChange={(e) => setRevistaActual({ ...revistaActual, estado: e.target.value })}
                        >
                          <option value="A">{t('common.active')}</option>
                          <option value="I">{t('common.inactive')}</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={cerrarModal}>
                    {t('common.cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        {t('journals.modal.saving')}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-save me-2"></i>
                        {t('common.save')}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevistasPage;
